import * as React from "react";
import { observer } from "mobx-react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";

import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { UitkFigureAspectRatioType as ExperimentalAspectRatioType } from "uitk-react-experimental-images";
import { UitkScrimType as UitkExperimentalScrimType } from "uitk-react-experimental-scrim";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";

import Campaign = CampaignRecommendationsWithOffersQuery.Campaigns;
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLink } from "uitk-react-link";

export interface CampaignVRProps {
  campaign: Campaign;
  campaignTitle?: string;
  imgSrc?: string;
  context?: ExtendedContextStore;
  seeMoreLinkUrl?: string;
}

export const CampaignVR = observer(({ campaign, campaignTitle, imgSrc, seeMoreLinkUrl }: CampaignVRProps) => {
  const campaignHeading = campaignTitle || campaign.headline;
  const image = imgSrc || "";
  const seeMoreUrl = seeMoreLinkUrl || "";
  const locSeeMoreKey = "propertyfilter.seeMore";
  const { formatText } = useLocalization();

  const scrimCardPropsSmall = {
    id: "vr-campaign-header",
    dataTestId: "vr-campaign-header",
    className: "",
    titleSlot: <h3 className="uitk-type-700">{campaignHeading}</h3>,
    subtitleSlot: (
      <UitkLayoutFlex direction="column" justifyContent="space-between" style={{ height: "inherit" }}>
        <UitkSpacing padding={{ small: { blockstart: "unset" } }}>
          <UitkLayoutFlexItem>
            <div>
              <UitkText size={500}>{campaign.description.long}</UitkText>
            </div>
          </UitkLayoutFlexItem>
        </UitkSpacing>
      </UitkLayoutFlex>
    ),
  };

  const mediumClassName = "vrScrim";
  const mediumSpacing: UitkSpacingProps = {
    padding: { blockend: "unset" },
  };
  const titleGrid: UitkLayoutConditionalGridColumnRow = { medium: 12 };
  const titleColSpan: UitkLayoutConditionalGridSpan = { medium: 7, large: 3 };

  const scrimCardPropsMedium = {
    id: "vr-campaign-header",
    dataTestId: "vr-campaign-header",
    className: mediumClassName,
    titleSlot: (
      <UitkLayoutGrid columns={titleGrid} space="two">
        <UitkLayoutGridItem colSpan={titleColSpan}>
          <UitkSpacing padding={{ inlinestart: "six" }}>
            <div>
              <Viewport>
                <ViewMedium>
                  <h3 className="uitk-type-700">{campaignHeading}</h3>
                </ViewMedium>
                <ViewLarge>
                  <h3 className="uitk-type-800">{campaignHeading}</h3>
                </ViewLarge>
              </Viewport>
            </div>
          </UitkSpacing>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    ),
    footerSlot: (
      <UitkCard className="vrFooter">
        <UitkCardContentSection>
          <UitkSpacing padding={{ inlinestart: "three", block: "three" }}>
            <UitkLayoutFlex>
              <UitkText size={300}>
                {campaign.description.long}
                {seeMoreUrl !== "" && (
                  <UitkLink inline>
                    <a href={seeMoreUrl} target="_blank" rel="noopener noreferrer">{` ${formatText(locSeeMoreKey)}`}</a>
                  </UitkLink>
                )}
              </UitkText>
            </UitkLayoutFlex>
          </UitkSpacing>
        </UitkCardContentSection>
      </UitkCard>
    ),
    spacing: mediumSpacing,
  };

  return (
    <Viewport>
      <ViewSmall>
        <ScrimCard
          {...scrimCardPropsSmall}
          aspectRatio={ExperimentalAspectRatioType.R1_1}
          backgroundImageURL={image}
          scrimType={UitkExperimentalScrimType.OVERLAY_TOP}
        />
      </ViewSmall>
      <ViewMedium>
        <ScrimCard
          {...scrimCardPropsMedium}
          aspectRatio={ExperimentalAspectRatioType.R4_1}
          backgroundImageURL={image}
          scrimType={UitkExperimentalScrimType.OVERLAY_LEFT}
        />
      </ViewMedium>
    </Viewport>
  );
});
